import { useRef, useState } from "react";
import { Plus, Pencil, Trash2, PenTool, Check, BookmarkPlus } from "lucide-react";
import { toast } from "sonner";
import { ShapeDesigner } from "./ShapeDesigner";
import { TemplatePicker } from "./TemplatePicker";
import { TemplateMetaDialog } from "./TemplateMetaDialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { uid } from "../../lib/format";

const DesignCard = ({ d, idx, onEdit, onDelete, onTemplate }) => (
  <div data-testid={`design-card-${idx}`} className="group overflow-hidden rounded-lg border border-zinc-200 bg-white transition-shadow hover:shadow-md">
    <div className="flex h-44 items-center justify-center overflow-hidden border-b border-zinc-100 bg-slate-50 p-3">
      {d.image ? <img src={d.image} alt={d.name} className="max-h-full max-w-full object-contain" /> : <PenTool className="h-8 w-8 text-zinc-300" />}
    </div>
    <div className="flex items-center justify-between gap-2 p-3">
      <div className="min-w-0">
        <div className="font-mono text-[10px] uppercase tracking-widest text-red-600">Design {idx + 1}</div>
        <div data-testid={`design-name-${idx}`} className="truncate font-semibold">{d.name}</div>
        <div className="text-xs text-zinc-400">{d.shapes.length} shape{d.shapes.length === 1 ? "" : "s"}</div>
      </div>
      <div className="flex">
        <button data-testid={`design-template-${idx}`} title="Save as template" onClick={onTemplate} className="rounded p-2 text-zinc-500 hover:bg-zinc-100"><BookmarkPlus className="h-4 w-4" /></button>
        <button data-testid={`design-edit-${idx}`} onClick={onEdit} className="rounded p-2 text-zinc-500 hover:bg-zinc-100"><Pencil className="h-4 w-4" /></button>
        <button data-testid={`design-delete-${idx}`} onClick={onDelete} className="rounded p-2 text-zinc-500 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  </div>
);

export const DesignsPanel = ({ designs, onChange, onAddLineItem, draft, setDraft }) => {
  const designer = useRef(null);
  const [tplFrom, setTplFrom] = useState(null);

  const startNew = () => setDraft({ id: uid(), name: `Design ${designs.length + 1}`, shapes: [], scale: 0.4, image: "", isNew: true });
  const commit = async () => {
    if (!draft.shapes.length) return toast.error("Add at least one shape to the design");
    const image = await designer.current.exportPng();
    const { isNew, ...d } = { ...draft, image, name: draft.name.trim() || "Untitled design" };
    onChange(isNew ? [...designs, d] : designs.map((x) => (x.id === d.id ? d : x)));
    toast.success(isNew ? "Design added to quote — remember to save the quote" : "Design updated — remember to save the quote");
    setDraft(null);
  };

  if (draft)
    return (
      <div data-testid="design-editor" className="space-y-3">
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-red-200 bg-red-50/60 p-3">
          <PenTool className="h-4 w-4 text-red-600" />
          <Input data-testid="design-name-input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="h-9 max-w-xs bg-white" placeholder="Design name" />
          <span className="text-xs text-zinc-500">{draft.isNew ? "New design" : "Editing design"} · one design at a time</span>
          <div className="ml-auto flex gap-2">
            <Button data-testid="design-cancel-button" variant="outline" onClick={() => setDraft(null)}>Cancel</Button>
            <Button data-testid="design-commit-button" onClick={commit} className="bg-red-600 hover:bg-red-700"><Check className="mr-1 h-4 w-4" />{draft.isNew ? "Add to quote" : "Update design"}</Button>
          </div>
        </div>
        <ShapeDesigner ref={designer} design={draft} onChange={(d) => setDraft((p) => ({ ...p, shapes: d.shapes, scale: d.scale }))} onAddLineItem={onAddLineItem} />
      </div>
    );

  return (
    <div data-testid="designs-panel">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-zinc-500">Each design is drawn separately and attached to the quote and its PDF.</p>
        <div className="flex gap-2">
          <TemplatePicker onPick={(d) => onChange([...designs, d])} />
          <Button data-testid="new-design-button" onClick={startNew} className="bg-red-600 hover:bg-red-700"><Plus className="mr-1 h-4 w-4" />New design</Button>
        </div>
      </div>
      {designs.length ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {designs.map((d, i) => (
            <DesignCard key={d.id} d={d} idx={i} onTemplate={() => setTplFrom(d)} onEdit={() => setDraft({ ...d, shapes: d.shapes.map((s) => ({ ...s, dims: { ...s.dims } })) })}
              onDelete={() => window.confirm(`Remove "${d.name}" from this quote?`) && onChange(designs.filter((x) => x.id !== d.id))} />
          ))}
        </div>
      ) : (
        <div className="grid place-items-center rounded-lg border-2 border-dashed border-zinc-200 bg-white py-14 text-center">
          <PenTool className="mb-2 h-8 w-8 text-zinc-300" />
          <div className="font-semibold">No designs yet</div>
          <div className="text-sm text-zinc-400">Create a pipe or fabrication design to attach to this quote.</div>
        </div>
      )}
      <TemplateMetaDialog design={tplFrom} onClose={() => setTplFrom(null)} />
    </div>
  );
};
